<template>
  <v-card class="my-6">
    <v-card-title>Reviewer Invitation</v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-tabs show-arrows v-model="reviewerSelectionType">
        <v-tab class="blue lighten-5"> JMESR Reviewer </v-tab>
        <v-tab class="blue lighten-5"> External Invitation </v-tab>
      </v-tabs>
      <v-tabs-items v-model="reviewerSelectionType">
        <v-tab-item>
          <div v-if="invites" class="mt-7 mb-3">
            <v-autocomplete
              outlined
              clearable
              item-value="_id"
              :disabled="!invites"
              :items="journalReviewers"
              :filter="journalReviewersFilter"
              label="Search for reviewer"
              v-model="selectedReviewer"
              prepend-inner-icon="mdi-account-supervisor"
            >
              <template v-slot:selection="data">
                {{ data.item.title }} {{ data.item.firstName }}
                {{ data.item.lastName }}
                <small class="ml-1" v-if="data.item.specialization">
                  ({{ data.item.specialization }})
                </small>
              </template>
              <template v-slot:item="data">
                <template v-if="typeof data.item !== 'object'">
                  <v-list-item-content v-text="data.item"></v-list-item-content>
                </template>
                <template v-else>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ data.item.title }}
                      {{ data.item.firstName }}
                      {{ data.item.lastName }}
                      <small class="ml-1" v-if="data.item.specialization">
                        ({{ data.item.specialization }})
                      </small>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ data.item.institution }} &dash;
                      {{ data.item.department }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>
            </v-autocomplete>

            <div v-if="selectedReviewer">
              <AuthorDetails class="mb-5" :author="reviewerProfile" />

              <v-textarea
                outlined
                clearable
                v-model="invitationNote"
                prepend-inner-icon="mdi-note-outline"
                label="Additional note (optional)"
              >
              </v-textarea>
            </div>

            <p
              v-if="pendingInviteToInternalReviewer(selectedReviewer)"
              class="red--text"
            >
              This reviewer has a pending invitation. Cannot invite while
              previous invitation is pending
            </p>

            <p v-else-if="pendingInvitation">
              There is pending invitation to review this manuscript
            </p>

            <v-btn
              type="submit"
              color="blue"
              class="white--text"
              @click="inviteReviewer"
              :loading="invitingReviewer"
              :disabled="
                !selectedReviewer ||
                invitingReviewer ||
                pendingInviteToInternalReviewer(selectedReviewer)
              "
            >
              Invite to Review Article
            </v-btn>
          </div>
          <h3 v-else class="my-5 red--text text-center">
            No new invitations allowed
          </h3>

          <ManuscriptInvitations
            :invitations="manuscript.invitations"
            class="my-6"
          />
        </v-tab-item>
        <v-tab-item>
          <v-card flat>
            <InviteExternalReviewer
              v-if="invites"
              :manuscript="manuscript._id"
              @invited="refreshManuscript"
            />
            <h3 v-else class="my-5 red--text text-center">
              No new invitations allowed
            </h3>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
import ManuscriptInvitations from "./ManuscriptInvitations";
import AuthorDetails from "../../Author/Includes/AuthorDetails";
import InviteExternalReviewer from "../../Review/Includes/InviteExternalReviewer";

export default {
  props: {
    manuscript: {
      type: Object,
      default: () => {},
    },
    pending: {
      type: Array,
      default: () => [],
    },
    invites: {
      type: Boolean,
      default: true,
    },
  },

  components: { AuthorDetails, ManuscriptInvitations, InviteExternalReviewer },

  data: () => ({
    invitationNote: null,
    invitingReviewer: false,
    journalReviewers: [],
    selectedReviewer: null,
    reviewerSelectionType: null,
    internalReviewersInvited: [],
  }),

  computed: {
    reviewerProfile() {
      return [...this.journalReviewers].find(
        (aut) => aut._id == this.selectedReviewer
      );
    },

    pendingInvitation() {
      return (
        [...this.manuscript.invitations].findIndex(
          (iv) => iv.status == "INVITED"
        ) > -1
      );
    },
  },

  methods: {
    journalReviewersFilter(item, queryText) {
      const email = item.email.toLowerCase();
      const specialization = item.specialization
        ? item.specialization.toLowerCase()
        : "";
      const institution = item.institution.toLowerCase();
      const department = item.department.toLowerCase();
      const firstName = item.firstName.toLowerCase();
      const lastName = item.lastName.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        email.indexOf(searchText) > -1 ||
        specialization.indexOf(searchText) > -1 ||
        institution.indexOf(searchText) > -1 ||
        department.indexOf(searchText) > -1 ||
        firstName.indexOf(searchText) > -1 ||
        lastName.indexOf(searchText) > -1
      );
    },
    pendingInviteToInternalReviewer(reviewer) {
      return !![...this.pending].find(
        (alreadyInvited) => alreadyInvited == reviewer
      );
    },
    fetchReviewers() {
      if (this.manuscript._id) {
        this.$axios
          .get("/editorial/reviewers")
          .then(({ data: response }) => {
            this.journalReviewers = response;
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not load authors",
            });
          });
      }
    },
    inviteReviewer() {
      if (!this.invitingReviewer) {
        this.invitingReviewer = true;

        const data = {
          manuscript: this.manuscript._id,
          reviewer: this.selectedReviewer,
          note: this.invitationNote,
        };

        this.$axios
          .post("/editorial/invitation", data)
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Reviewer invited",
            });
            this.selectedReviewer = null;
            this.refreshManuscript();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not invite reviewer",
            });
          })
          .finally(() => {
            this.invitingReviewer = false;
          });
      }
    },
    refreshManuscript() {
      this.$emit("refresh");
    },
  },

  mounted() {
    this.fetchReviewers();
  },
};
</script>